import { Button, formatPhone, PageError, PageLoading, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Select } from "@majitap/ui/arc/select/select";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { messageFor } from "../../error-copy";
import { fleetActions, listQueries, PROFILE_STATUS, TRUCK_STATUS, useRefreshFleet } from "../../fleet";
import { APPROVAL_STATUS } from "../../labels";
import { RouterLink } from "../../router-link";
import { ConfirmDialog } from "../shared/confirm";
import styles from "./partners.module.css";

type Kind = "truck" | "driver" | "fillsite" | "assistant";
const KIND: Record<Kind, { one: string; the: string }> = {
  truck: { one: "a truck", the: "truck" },
  driver: { one: "a driver", the: "driver" },
  fillsite: { one: "a fill site", the: "fill site" },
  assistant: { one: "an assistant", the: "assistant" },
};
type Asset = { kind: Kind; id: string; label: string };

/** Moves an asset or a driver to a partner, or back to the company's own fleet (null), P8-42. */
const setOwner = (asset: Pick<Asset, "kind" | "id">, partnerId: string | null): Promise<unknown> => {
  const body = { partner_id: partnerId };
  if (asset.kind === "truck") return fleetActions.updateTruck(asset.id, body);
  if (asset.kind === "driver") return fleetActions.updateDriver(asset.id, body);
  if (asset.kind === "fillsite") return fleetActions.updateFillsite(asset.id, body);
  return fleetActions.updateAssistant(asset.id, body);
};

/**
 * The partner's trucks, drivers, fill sites and assistants. The partner manages them in the
 * portal; staff approve its drivers and fill sites on Fleet. Staff with `fleet.manage` attach the
 * company's own trucks, drivers, fill sites and assistants to the partner, or detach them (P8-42).
 */
export function PartnerFleet({ partnerId, canAttach }: { partnerId: string; canAttach: boolean }): ReactNode {
  const query = { partner_id: partnerId, limit: 100 };
  const trucks = useQuery({
    queryKey: ["fleet", "trucks", "partner", partnerId],
    queryFn: async () => (await ok(api.GET("/v1/admin/fleet/trucks", { params: { query } }))).data,
  });
  const drivers = useQuery({
    queryKey: ["fleet", "drivers", "partner", partnerId],
    queryFn: async () => (await ok(api.GET("/v1/admin/fleet/drivers", { params: { query } }))).data,
  });
  const sites = useQuery(listQueries.fillsites);
  const assistants = useQuery(listQueries.assistants);
  const [attaching, setAttaching] = useState<Kind | null>(null);
  const [detaching, setDetaching] = useState<Asset | null>(null);
  const refresh = useRefreshFleet();
  const failed = [trucks, drivers, sites, assistants].find((q) => q.error);
  if (failed) return <PageError message={messageFor(errorOf(failed.error))} onRetry={() => failed.refetch()} />;
  if (!trucks.data || !drivers.data || !sites.data || !assistants.data)
    return <PageLoading label="Loading the fleet" />;
  const theirs = <T extends { partner_id: string | null }>(rows: T[]) => rows.filter((r) => r.partner_id === partnerId);

  const heading = (title: string, kind?: Kind) => (
    <div className={styles.actions}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {canAttach && kind ? (
        <Button variant="secondary" size="sm" onClick={() => setAttaching(kind)}>
          Attach {KIND[kind].one}
        </Button>
      ) : null}
    </div>
  );

  return (
    <div className={styles.panel}>
      <p className={styles.muted}>
        The partner manages its fleet in the partner portal. Approve its drivers and fill sites on{" "}
        <RouterLink href="/fleet?view=drivers" className={styles.link}>
          Fleet
        </RouterLink>
        .
        {canAttach ? " Attach the company's own trucks, drivers, fill sites and assistants to the partner here." : null}
      </p>
      <section className={styles.section}>
        {heading("Trucks", "truck")}
        <SortableDataTable
          caption="Trucks"
          rowKey="id"
          rows={trucks.data}
          itemName={{ one: "truck", other: "trucks" }}
          emptyMessage="No trucks yet"
          columns={[
            { key: "plate_number", label: "Plate", sortable: false },
            {
              key: "capacity_litres",
              label: "Capacity",
              sortable: false,
              render: (_v, t) => `${t.capacity_litres.toLocaleString("en-KE")} L`,
            },
            {
              key: "status",
              label: "Status",
              sortable: false,
              render: (_v, t) => <Badge tone={TRUCK_STATUS[t.status][1]}>{TRUCK_STATUS[t.status][0]}</Badge>,
            },
            ...(canAttach
              ? [
                  {
                    key: "detach",
                    label: "",
                    sortable: false,
                    render: (_v: unknown, t: (typeof trucks.data)[number]) => (
                      <TextButton onClick={() => setDetaching({ kind: "truck", id: t.id, label: t.plate_number })}>
                        Detach
                      </TextButton>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </section>
      <section className={styles.section}>
        {heading("Drivers", "driver")}
        <SortableDataTable
          caption="Drivers"
          rowKey="id"
          rows={drivers.data}
          itemName={{ one: "driver", other: "drivers" }}
          emptyMessage="No drivers yet"
          columns={[
            { key: "user", label: "Driver", sortable: false, render: (_v, d) => d.user.name },
            {
              key: "phone",
              label: "Phone",
              sortable: false,
              render: (_v, d) => formatPhone(d.user.phone_number) || "—",
            },
            {
              key: "approval_status",
              label: "Approval",
              sortable: false,
              render: (_v, d) => (
                <Badge tone={APPROVAL_STATUS[d.approval_status][1]}>{APPROVAL_STATUS[d.approval_status][0]}</Badge>
              ),
            },
            ...(canAttach
              ? [
                  {
                    key: "detach",
                    label: "",
                    sortable: false,
                    render: (_v: unknown, d: (typeof drivers.data)[number]) => (
                      <TextButton onClick={() => setDetaching({ kind: "driver", id: d.id, label: d.user.name })}>
                        Detach
                      </TextButton>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </section>
      <section className={styles.section}>
        {heading("Fill sites", "fillsite")}
        <SortableDataTable
          caption="Fill sites"
          rowKey="id"
          rows={theirs(sites.data)}
          itemName={{ one: "fill site", other: "fill sites" }}
          emptyMessage="No fill sites"
          columns={[
            { key: "name", label: "Fill site", sortable: false },
            {
              key: "geofence",
              label: "Service zone",
              sortable: false,
              render: (_v, f) => f.geofence?.name ?? "Outside zones",
            },
            {
              key: "approval_status",
              label: "Approval",
              sortable: false,
              render: (_v, f) => (
                <Badge tone={APPROVAL_STATUS[f.approval_status][1]}>{APPROVAL_STATUS[f.approval_status][0]}</Badge>
              ),
            },
            ...(canAttach
              ? [
                  {
                    key: "detach",
                    label: "",
                    sortable: false,
                    render: (_v: unknown, f: (typeof sites.data)[number]) => (
                      <TextButton onClick={() => setDetaching({ kind: "fillsite", id: f.id, label: f.name })}>
                        Detach
                      </TextButton>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </section>
      <section className={styles.section}>
        {heading("Assistants", "assistant")}
        <SortableDataTable
          caption="Assistants"
          rowKey="id"
          rows={theirs(assistants.data)}
          itemName={{ one: "assistant", other: "assistants" }}
          emptyMessage="No assistants"
          columns={[
            { key: "name", label: "Assistant", sortable: false },
            {
              key: "status",
              label: "Status",
              sortable: false,
              render: (_v, a) => <Badge tone={PROFILE_STATUS[a.status][1]}>{PROFILE_STATUS[a.status][0]}</Badge>,
            },
            ...(canAttach
              ? [
                  {
                    key: "detach",
                    label: "",
                    sortable: false,
                    render: (_v: unknown, a: (typeof assistants.data)[number]) => (
                      <TextButton onClick={() => setDetaching({ kind: "assistant", id: a.id, label: a.name })}>
                        Detach
                      </TextButton>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </section>
      {attaching ? (
        <AttachDialog
          kind={attaching}
          partnerId={partnerId}
          own={
            attaching === "truck" || attaching === "driver"
              ? null
              : (attaching === "fillsite" ? sites.data : assistants.data)
                  .filter((r) => r.partner_id === null)
                  .map((r) => ({ kind: attaching, id: r.id, label: r.name }))
          }
          onClose={() => setAttaching(null)}
        />
      ) : null}
      {detaching ? (
        <ConfirmDialog
          title={`Detach ${detaching.label}?`}
          description={`The ${KIND[detaching.kind].the} goes back to the company's own fleet. Not while it is on a shift or a delivery not finished yet.`}
          action="Detach"
          run={() => setOwner(detaching, null)}
          onDone={refresh}
          onClose={() => setDetaching(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Attach one of the company's own trucks, drivers, fill sites or assistants to the partner.
 * Trucks and drivers are paginated, so the company's own are fetched here (the first 100).
 */
function AttachDialog({
  kind,
  partnerId,
  own,
  onClose,
}: {
  kind: Kind;
  partnerId: string;
  own: Asset[] | null;
  onClose: () => void;
}) {
  const refresh = useRefreshFleet();
  const ownPaged = useQuery({
    queryKey: ["fleet", kind === "driver" ? "drivers" : "trucks", "own"],
    enabled: own === null,
    queryFn: async (): Promise<Asset[]> => {
      const query = { params: { query: { limit: 100 } } };
      return kind === "driver"
        ? (await ok(api.GET("/v1/admin/fleet/drivers", query))).data
            .filter((d) => d.partner_id === null)
            .map((d) => ({ kind, id: d.id, label: d.user.name }))
        : (await ok(api.GET("/v1/admin/fleet/trucks", query))).data
            .filter((t) => t.partner_id === null)
            .map((t) => ({ kind, id: t.id, label: t.plate_number }));
    },
  });
  const options = own ?? ownPaged.data ?? [];
  const [assetId, setAssetId] = useState("");
  const attach = useMutation({
    mutationFn: () => setOwner({ kind, id: assetId }, partnerId),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Attach ${KIND[kind].one}`}
        description={`One of the company's own. The partner then sees it in its portal and is paid for its work. Not while it is on a shift or a delivery not finished yet.`}
      >
        <div className={styles.form}>
          {own === null && ownPaged.isPending ? (
            <PageLoading label={`Loading ${KIND[kind].the}s`} />
          ) : options.length === 0 ? (
            <Alert tone="info" title={`No ${KIND[kind].the} to attach`}>
              Every one belongs to a partner already. Add one on Fleet, choosing this partner as its owner.
            </Alert>
          ) : (
            <Select
              label={KIND[kind].the[0].toUpperCase() + KIND[kind].the.slice(1)}
              placeholder="Choose one"
              value={assetId}
              onValueChange={setAssetId}
              options={options.map((o) => ({ value: o.id, label: o.label }))}
            />
          )}
          {attach.error ? (
            <Alert tone="danger" title="Not attached">
              {messageFor(errorOf(attach.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!assetId} loading={attach.isPending} onClick={() => attach.mutate()}>
              Attach
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
